import { css, html, LitElement } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { actions, ColorTypes } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { Code } from "@omegagrid/code";
import { ToolbarItem } from "@omegagrid/toolbar";

const EDITOR_OPTIONS = { readOnly: true };

/**
 * Read-only view of a compiled SQL query. The application prepends a header (use, variables,
 * parameters, ...) and appends a footer (temp table drops) to the user's code, so the line
 * numbers of the full output match neither the editor nor the reported error lines - the
 * toolbar toggle cuts both off so they do.
 */
@customElement('rp-editor-sql-compile')
export class SqlCompileView extends LitElement {

	static styles = css`
		:host {
			display: flex;
			position: relative;
			flex-direction: column;
			height: 100%;
		}

		* {
			box-sizing: border-box;
		}

		.controls {
			display: flex;
			flex-direction: row;
			align-items: center;
			flex: 0;
			border-bottom: 1px solid var(--og-bucket-border-color);
		}

		.body {
			flex: 1;
			overflow: auto;
			position: relative;
		}
	`;

	/** the full compiled SQL, including the header the application adds before the user's code */
	@property({type: String})
	sql = '';

	/** number of header lines before the user's code (0 = nothing to trim, the toolbar is hidden) */
	@property({type: Number})
	offset = 0;

	/** number of footer lines after the user's code */
	@property({type: Number})
	tail = 0;

	@state()
	private trim = false;

	@query('og-code')
	code: Code;

	get content() {
		if (!this.trim || this.offset <= 0) return this.sql;
		const lines = this.sql.split('\n');
		return lines.slice(this.offset, this.tail > 0 ? lines.length - this.tail : undefined).join('\n');
	}

	get toolbarItems() : ToolbarItem[] {
		return [{
			type: 'togglebutton',
			id: 'trim',
			text: msg('Trim'),
			icon: {icon: ['fas', 'scissors']},
			color: ColorTypes.Accent,
			value: this.trim
		}];
	}

	_onToolbarAction(e: actions.ActionEvent) {
		if (e.action.key === 'trim') this.trim = !!e.action.value;
	}

	render = () => html`
		${this.offset > 0 ? html`
			<div class="controls">
				<og-toolbar
					.items="${this.toolbarItems}"
					@toolbar.action="${(e: actions.ActionEvent) => this._onToolbarAction(e)}">
				</og-toolbar>
			</div>
		` : ''}

		<div class="body">
			<og-code
				language="sql"
				.content="${this.content}"
				.options="${EDITOR_OPTIONS}">
			</og-code>
		</div>
	`;

	layout() {
		this.code?.layout();
	}

}
